import type { ReactNode } from 'react';
import {
  LayoutDashboard, Package, Users, UserCheck, Truck, ShoppingCart, ShoppingBag,
  Warehouse, Banknote, Landmark, BookCheck, FileText, Receipt, Settings,
  Building2, Colorfilter, ClipboardList, Plus,
  Database,
} from '@/components/icons';

export type QuickLinkGroup = 'navigate' | 'create';

export interface QuickLinkCatalogEntry {
  key: string;
  path: string;
  /** i18n key resolved at render time. */
  labelKey: string;
  icon: ReactNode;
  group: QuickLinkGroup;
}

const I = 16;

/**
 * Pages that can be promoted to the top-header quick-link bar. Keys are stable
 * so user preferences survive label/path renames (only paths need updating
 * here if routes move).
 *
 * `create.*` entries either point to a dedicated `/new` route (sales &
 * purchase documents) or to a list page with `?new=1` which auto-opens the
 * creation dialog (articles, clients, suppliers, representatives).
 */
export const QUICK_LINK_CATALOG: QuickLinkCatalogEntry[] = [
  { key: 'dashboard', path: '/', labelKey: 'nav.dashboard', icon: <LayoutDashboard size={I} />, group: 'navigate' },

  { key: 'articles', path: '/articles', labelKey: 'nav.articles', icon: <Package size={I} />, group: 'navigate' },
  { key: 'clients', path: '/clients', labelKey: 'nav.clients', icon: <Users size={I} />, group: 'navigate' },
  { key: 'representatives', path: '/representatives', labelKey: 'nav.representatives', icon: <UserCheck size={I} />, group: 'navigate' },
  { key: 'suppliers', path: '/suppliers', labelKey: 'nav.suppliers', icon: <Truck size={I} />, group: 'navigate' },

  { key: 'pos', path: '/caisse/pos', labelKey: 'nav.posScreen', icon: <Receipt size={I} />, group: 'navigate' },

  { key: 'sales.quotes', path: '/sales/quotes', labelKey: 'nav.quotes', icon: <ShoppingCart size={I} />, group: 'navigate' },
  { key: 'sales.orders', path: '/sales/orders', labelKey: 'nav.orders', icon: <ShoppingCart size={I} />, group: 'navigate' },
  { key: 'sales.deliveries', path: '/sales/deliveries', labelKey: 'nav.deliveries', icon: <ShoppingCart size={I} />, group: 'navigate' },
  { key: 'sales.invoices', path: '/sales/invoices', labelKey: 'nav.invoices', icon: <ShoppingCart size={I} />, group: 'navigate' },
  { key: 'sales.credit-notes', path: '/sales/credit-notes', labelKey: 'nav.creditNotes', icon: <ShoppingCart size={I} />, group: 'navigate' },

  { key: 'purchases.orders', path: '/purchases/orders', labelKey: 'nav.purchaseOrders', icon: <ShoppingBag size={I} />, group: 'navigate' },
  { key: 'purchases.receptions', path: '/purchases/receptions', labelKey: 'nav.receptions', icon: <ShoppingBag size={I} />, group: 'navigate' },
  { key: 'purchases.invoices', path: '/purchases/invoices', labelKey: 'nav.purchaseInvoices', icon: <ShoppingBag size={I} />, group: 'navigate' },
  { key: 'purchases.credit-notes', path: '/purchases/credit-notes', labelKey: 'nav.purchaseCreditNotes', icon: <ShoppingBag size={I} />, group: 'navigate' },

  { key: 'stock', path: '/stock', labelKey: 'nav.stockMovements', icon: <Warehouse size={I} />, group: 'navigate' },
  { key: 'stock.exits', path: '/stock/vehicle-exits', labelKey: 'nav.stockExits', icon: <Warehouse size={I} />, group: 'navigate' },
  { key: 'stock.entries', path: '/stock/vehicle-entries', labelKey: 'nav.stockEntries', icon: <Warehouse size={I} />, group: 'navigate' },
  { key: 'warehouses', path: '/stock/warehouses', labelKey: 'nav.warehouses', icon: <Warehouse size={I} />, group: 'navigate' },

  { key: 'payments', path: '/payments', labelKey: 'nav.paymentsNav', icon: <Banknote size={I} />, group: 'navigate' },
  { key: 'treasury', path: '/treasury', labelKey: 'nav.treasuryNav', icon: <Landmark size={I} />, group: 'navigate' },
  { key: 'checks', path: '/checks', labelKey: 'nav.checksOnly', icon: <BookCheck size={I} />, group: 'navigate' },
  { key: 'traites', path: '/traites', labelKey: 'nav.traites', icon: <BookCheck size={I} />, group: 'navigate' },
  { key: 'withholding.sales', path: '/withholding/sales', labelKey: 'nav.withholdingSales', icon: <FileText size={I} />, group: 'navigate' },
  { key: 'withholding.purchases', path: '/withholding/purchases', labelKey: 'nav.withholdingPurchases', icon: <FileText size={I} />, group: 'navigate' },

  { key: 'settings', path: '/settings', labelKey: 'nav.settings', icon: <Settings size={I} />, group: 'navigate' },
  { key: 'settings.company', path: '/settings/company', labelKey: 'nav.settingsCompany', icon: <Building2 size={I} />, group: 'navigate' },
  { key: 'settings.branding', path: '/settings/branding', labelKey: 'nav.settingsBranding', icon: <Colorfilter size={I} />, group: 'navigate' },
  { key: 'settings.databaseImport', path: '/settings/database-import', labelKey: 'nav.settingsDatabaseImport', icon: <Database size={I} />, group: 'navigate' },
  { key: 'settings.pdfTemplates', path: '/settings/pdf-templates', labelKey: 'nav.settingsPdfTemplates', icon: <FileText size={I} />, group: 'navigate' },
  { key: 'settings.articleTickets', path: '/settings/article-tickets', labelKey: 'nav.settingsArticleTickets', icon: <ClipboardList size={I} />, group: 'navigate' },

  // Create actions — articles / contacts rely on the list page's ?new=1 flag.
  { key: 'create.article', path: '/articles?new=1', labelKey: 'quickLinks.create.article', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.client', path: '/clients?new=1', labelKey: 'quickLinks.create.client', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.supplier', path: '/suppliers?new=1', labelKey: 'quickLinks.create.supplier', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.representative', path: '/representatives?new=1', labelKey: 'quickLinks.create.representative', icon: <Plus size={I} />, group: 'create' },

  // Create actions — sales & purchase documents use dedicated /new routes.
  { key: 'create.quote', path: '/sales/quotes/new', labelKey: 'quickLinks.create.quote', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.salesOrder', path: '/sales/orders/new', labelKey: 'quickLinks.create.salesOrder', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.delivery', path: '/sales/deliveries/new', labelKey: 'quickLinks.create.delivery', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.invoice', path: '/sales/invoices/new', labelKey: 'quickLinks.create.invoice', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.creditNote', path: '/sales/credit-notes/new', labelKey: 'quickLinks.create.creditNote', icon: <Plus size={I} />, group: 'create' },

  { key: 'create.purchaseOrder', path: '/purchases/orders/new', labelKey: 'quickLinks.create.purchaseOrder', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.reception', path: '/purchases/receptions/new', labelKey: 'quickLinks.create.reception', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.purchaseInvoice', path: '/purchases/invoices/new', labelKey: 'quickLinks.create.purchaseInvoice', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.purchaseCreditNote', path: '/purchases/credit-notes/new', labelKey: 'quickLinks.create.purchaseCreditNote', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.stockExit', path: '/stock/vehicle-exits/new', labelKey: 'quickLinks.create.stockExit', icon: <Plus size={I} />, group: 'create' },
  { key: 'create.stockEntry', path: '/stock/vehicle-entries/new', labelKey: 'quickLinks.create.stockEntry', icon: <Plus size={I} />, group: 'create' },
];

export function findCatalogEntry(key: string): QuickLinkCatalogEntry | undefined {
  return QUICK_LINK_CATALOG.find((e) => e.key === key);
}

